import { RiskRecordsTable } from '@src/entities/RiskRecord/ui/components/tables/RiskRecords';
import { Chart } from '@src/shared/ui/components/Chart';
import { chartTypes } from '@src/shared/ui/components/Chart/includes/chartTypes';
import { ErrorHandler } from '@src/shared/ui/components/ErrorHandler';
import { Widget } from '@src/shared/ui/components/Widget';
import { Box } from '@uhg-abyss/web/ui/Box';
import { isEmpty, isEqual, sortBy } from 'lodash';
import React, { useEffect, useMemo, useState } from 'react';

import { Styles } from './includes/styles';

/**
 * Widget: AggregationView
 *
 * @TODO Needs description.
 *
 * @param aggregation
 * @param criteria
 * @param view
 * @returns {Element}
 * @constructor
 */
export function AggregationView({
  aggregation = {
    aggregationResults: [],
    researchAggregation: {
      chartTypes: [],
      dataTableColumnHeaders: {},
      defaultChartType: '',
    },
  },
  criteria = {
    entrance: {
      additional: [],
      commonCriteriaVersionsIds: [],
      commonIds: [],
    },
    exit: {
      additional: [],
      commonCriteriaVersionsIds: [],
      commonIds: [],
    },
  },
  view = {
    description: '',
    id: '',
    name: '',
  },
}) {
  const [currentChartType, setCurrentChartType] = useState(aggregation?.researchAggregation?.defaultChartType);
  const [data, setData] = useState(aggregation?.aggregationResults);

  let widgetMenuOptions = [];

  if (!view?.id?.includes('risk-records')) {
    widgetMenuOptions = [
      {
        label: 'View as...',
        onChange: setCurrentChartType,
        radios: aggregation?.researchAggregation?.chartTypes.map((chartType) => {
          return {
            label: chartTypes?.[chartType]?.label,
            value: chartType,
          };
        }),
        title: 'View as...',
        value: currentChartType,
      },
    ];
  }

  /**
   * Custom sorting based on view.
   */
  useEffect(() => {
    let theData = aggregation?.aggregationResults;

    if (['last-reopened-date', 'last-streamed-date'].includes(view?.id)) {
      const order = ['1 week', '1 week - 1 month', '1 month - 3 months', '3 months - 9 months', '> 9 months'];

      theData = sortBy(aggregation?.aggregationResults, (item) => {
        return order.indexOf(String(item.name).toLowerCase());
      });
    }

    if (!isEqual(data, theData)) {
      setData(theData);
    }
  }, [view?.id, aggregation?.aggregationResults]);

  /**
   * tableColumns
   *
   * Map the table columns based on the aggregation data.
   *
   * @type {{initialColumns: *[], initialState: {sortBy: [{desc: boolean, id: string}]}}}
   */
  const tableConfiguration = useMemo(() => {
    const theConfiguration = {
      initialColumns: [],
      initialState: {
        sortBy: [
          {
            desc: true,
            id: 'value',
          },
        ],
      },
    };

    if (!isEmpty(aggregation?.researchAggregation?.dataTableColumnHeaders)) {
      Object.keys(aggregation?.researchAggregation?.dataTableColumnHeaders).forEach((accessor) => {
        theConfiguration.initialColumns.push({
          accessor,
          Cell: ({ cell }) => {
            // Formats number to display with commas.
            if (accessor === 'value') {
              return <React.Fragment>{Number(cell?.value).toLocaleString('en-US')}</React.Fragment>;
            }

            // Formats a number to display as a decimal.
            if (accessor === 'perc') {
              return <React.Fragment>{cell?.value}%</React.Fragment>;
            }

            return cell?.value;
          },

          Header: aggregation?.researchAggregation?.dataTableColumnHeaders?.[accessor],
        });
      });
    }

    return theConfiguration;
  }, [aggregation?.researchAggregation?.dataTableColumnHeaders]);
  return (
    <ErrorHandler location="src/widgets/RiskRecords/AggregationView/AggregationView.jsx">
      <Styles css={view?.id === 'risk-records' && { width: '100%' }}>
        <Box color="" height="" key={view?.id}>
          <Widget description={view?.description || ''} menu={widgetMenuOptions || []} title={view?.name || ''}>
            {view?.id === 'risk-records' ? (
              <RiskRecordsTable allowExport={false} criteria={criteria} showHeaderLeft={false} showHeaderTop={false} />
            ) : (
              <Chart data={data} legendLimit={15} tableConfiguration={tableConfiguration} type={currentChartType} />
            )}
          </Widget>
        </Box>
      </Styles>
    </ErrorHandler>
  );
}
